import { AttributeValue, DynamoDBClient, GetItemCommand, PutItemCommand, UpdateItemCommand } from "@aws-sdk/client-dynamodb";
import { marshall, unmarshall } from "@aws-sdk/util-dynamodb";
import { useCallback, useState } from "react";

interface Params {
  tableName: string;
  client: DynamoDBClient;
}

export const useDynamoDB = ({ tableName, client }: Params) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const getItem = useCallback(
    async (key: Record<string, any>) => {
      setLoading(true);
      setError(null);
      try {
        const command = new GetItemCommand({
          TableName: tableName,
          Key: marshall(key),
        });
        const response = await client.send(command);
        if (!response.Item) {
          return null;
        }
        return unmarshall(response.Item as Record<string, AttributeValue>);
      } catch (err) {
        setError(err as Error);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [client, tableName]
  );

  const putItem = useCallback(
    async (item: Record<string, any>) => {
      setLoading(true);
      setError(null);
      try {
        const command = new PutItemCommand({
          TableName: tableName,
          Item: marshall(item),
        });
        const response = await client.send(command);
        return response;
      } catch (err) {
        setError(err as Error);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [client, tableName]
  );

  const updateItem = useCallback(
    async (key: Record<string, any>, updates: Record<string, any>) => {
      const updateExpressions = [];
      const expressionAttributeNames: Record<string, string> = {};
      const expressionAttributeValues: Record<string, any> = {};
      for (const [field, value] of Object.entries(updates)) {
        updateExpressions.push(`#${field} = :${field}`);
        expressionAttributeNames[`#${field}`] = field;
        expressionAttributeValues[`:${field}`] = value;
      }
      const command = new UpdateItemCommand({
        TableName: tableName,
        Key: marshall(key),
        UpdateExpression: `SET ${updateExpressions.join(", ")}`,
        ExpressionAttributeNames: expressionAttributeNames,
        ExpressionAttributeValues: marshall(expressionAttributeValues),
      });
      await client.send(command);
    },
    [client, tableName]
  );

  return {
    loading,
    error,
    getItem,
    putItem,
    updateItem,
  };
};
